import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import { Shield, UserPlus } from "react-feather";
import { useGetUpcomingDeploymentsQuery, useGetGuardRequirementsForOrderQuery } from "../guardingApi";
import { useSetGuardDeploymentMutation, useUpdateDeploymentLineMutation } from "../../sales/salesApi";
import { useCanEditDeployment } from "../../sales/useSalesPermissions";
import { paths } from "../../../routes/paths";
import DeploymentContextCards from "./DeploymentContextCards";
import CreateAssignmentsFromDeploymentModal from "./CreateAssignmentsFromDeploymentModal";
import GuardRosterModal from "./roster/GuardRosterModal";
import { dateOnly, isWholeOrderRequest, linesForRequest, shiftLabel } from "../pendingRequest";

const titleCase = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s);

// One deployment REQUEST - the lines of an order that share a start date and a
// guard service (see pendingRequest.js). The deployment details (date, number,
// remarks) are the same for the whole request, so they are entered once and
// stamped on each of its lines. Deployed quantities are per line, so part of a
// request can go out now and the rest later - it stays pending until every line
// in it is full.
//
// The API takes the ORDER off the pending list only when its last line is
// fully deployed (SalesService.UpdateGuardDeploymentLineAsync writes the
// order-level deployment row at that moment), so an order with a later start
// date or another service keeps its other requests here after this one goes.
//
// AI Guard Roster opens as its own modal rather than a tab - the split-view
// detail pane was too narrow for its filters/table/selection all at once,
// per direct instruction after trying the tab version live.
const PendingDeploymentDetail = () => {
  const { recordGuid } = useParams();
  const [searchParams] = useSearchParams();
  const dateParam = searchParams.get("date") || "";
  const serviceParam = searchParams.get("service") || "";
  const navigate = useNavigate();
  const canEdit = useCanEditDeployment();

  const { data: rows = [] } = useGetUpcomingDeploymentsQuery();
  const sameOrder = rows.filter((r) => String(r.recordGuid).toLowerCase() === String(recordGuid).toLowerCase());
  const request =
    sameOrder.find(
      (r) => dateOnly(r.requestedDeploymentDate) === dateParam && String(r.serviceTypeId ?? "") === serviceParam
    ) || (dateParam || serviceParam ? null : sameOrder[0]);

  const { data: orderLines = [], isLoading: linesLoading } = useGetGuardRequirementsForOrderQuery(recordGuid, {
    skip: !request,
  });
  const lines = useMemo(() => linesForRequest(orderLines, request), [orderLines, request]);

  const [setGuardDeployment, { isLoading: isSavingWhole }] = useSetGuardDeploymentMutation();
  const [updateDeploymentLine] = useUpdateDeploymentLineMutation();
  const [isSavingLines, setIsSavingLines] = useState(false);
  const [showRoster, setShowRoster] = useState(false);
  const [showCreateAssignments, setShowCreateAssignments] = useState(false);

  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: { deploymentDate: dayjs().format("YYYY-MM-DD"), deploymentNumber: "", remarks: "" },
  });

  // Deployed quantity per line, as typed. Opens at each line's full Qty - the
  // usual case is the whole request going out together - and can be lowered
  // for a partial deployment.
  const [deployedQty, setDeployedQty] = useState({});
  const lineIdsKey = lines.map((l) => `${l.lineId}:${l.deployedQty}`).join(",");
  useEffect(() => {
    setDeployedQty(Object.fromEntries(lines.map((l) => [l.lineId, String(l.qty)])));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineIdsKey]);
  useEffect(() => {
    reset({ deploymentDate: dayjs().format("YYYY-MM-DD"), deploymentNumber: "", remarks: "" });
  }, [recordGuid, dateParam, serviceParam, reset]);

  if (!request) {
    return <div className="p-4 text-muted">This deployment request is no longer pending.</div>;
  }

  const wholeOrder = isWholeOrderRequest(request);

  const onSubmitLines = async (values) => {
    const changes = lines
      .map((l) => ({ line: l, qty: Number(deployedQty[l.lineId]) }))
      .filter(({ line, qty }) => Number.isFinite(qty) && qty !== line.deployedQty);

    const bad = changes.find(({ line, qty }) => qty < 0 || qty > line.qty);
    if (bad) return toast.error(`Line ${bad.line.lineNumber}: deployed must be between 0 and ${bad.line.qty}`);
    if (changes.length === 0) return toast.error("Nothing to record - the deployed quantities are unchanged");

    setIsSavingLines(true);
    try {
      // One line at a time: the API decides on each call whether the order is
      // now fully deployed, so the last one has to see the others already saved.
      for (const { line, qty } of changes) {
        // eslint-disable-next-line no-await-in-loop
        await updateDeploymentLine({
          recordGuid: request.recordGuid,
          lineId: line.lineId,
          deployedQty: qty,
          remarks: values.remarks?.trim() || line.remarks || null,
          deploymentDate: values.deploymentDate,
          deploymentNumber: values.deploymentNumber?.trim() || null,
        }).unwrap();
      }
      const requestComplete = lines.every((l) => {
        const change = changes.find((c) => c.line.lineId === l.lineId);
        return (change ? change.qty : l.deployedQty) >= l.qty;
      });
      if (requestComplete) {
        toast.success("Deployment recorded");
        navigate(paths.guardingPendingDeployments);
      } else {
        toast.success("Partial deployment recorded - the rest stays pending");
      }
    } catch (error) {
      toast.error(error?.message || "Could not record the guard deployment");
    } finally {
      setIsSavingLines(false);
    }
  };

  // Only for an order with no deployment lines at all - marks the whole order.
  const onSubmitWhole = async (values) => {
    try {
      await setGuardDeployment({
        recordGuid: request.recordGuid,
        deploymentDate: values.deploymentDate,
        deploymentNumber: values.deploymentNumber || null,
        remarks: values.remarks || null,
      }).unwrap();
      toast.success("Guard deployment recorded");
      navigate(paths.guardingPendingDeployments);
    } catch (error) {
      toast.error(error?.message || "Could not record the guard deployment");
    }
  };

  const isSaving = isSavingLines || isSavingWhole;
  const otherRequests = sameOrder.length - 1;
  const types = [...new Set(lines.map((l) => l.deploymentType))];

  return (
    <div className="p-4">
      <div className="d-flex align-items-start justify-content-between mb-1">
        <h4 className="mb-0">{request.orderNumber}</h4>
        <div className="d-flex gap-2">
          {/* Creating the customer's posts is planning; Mark Deployed below is a
              fact about the world. Two steps on purpose - and this one can also
              be done from the AI Guard Roster. */}
          {canEdit && (
            <button
              type="button"
              className="btn btn-outline-primary d-inline-flex align-items-center gap-2"
              onClick={() => setShowCreateAssignments(true)}
            >
              <UserPlus size={16} /> Create Assignments
            </button>
          )}
          <button type="button" className="btn btn-primary d-inline-flex align-items-center gap-2" onClick={() => setShowRoster(true)}>
            <Shield size={16} /> Open AI Guard Roster
          </button>
        </div>
      </div>
      <p className="text-muted mb-3">
        Enquiry {request.enquiryNumber}
        {request.serviceTypeName ? ` · ${request.serviceTypeName}` : ""}
        {otherRequests > 0 && (
          <span className="badge badge-linelight ms-2">
            +{otherRequests} other request{otherRequests === 1 ? "" : "s"} on this order
          </span>
        )}
      </p>

      <DeploymentContextCards
        order={request}
        guardsCount={request.guardsCount}
        deploymentType={types.length === 1 ? types[0] : null}
        shift={lines.length ? shiftLabel(lines) : null}
        requestedDate={request.requestedDeploymentDate}
      />

      {!wholeOrder && (
        <div className="card mb-3">
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Shift</th>
                    <th>Type</th>
                    <th>Schedule</th>
                    <th className="text-end">Required</th>
                    <th style={{ width: 130 }}>Deployed</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {linesLoading && (
                    <tr><td colSpan={7} className="text-muted py-3 text-center">Loading lines...</td></tr>
                  )}
                  {lines.map((l) => (
                    <tr key={l.lineId}>
                      <td>{l.lineNumber}</td>
                      <td>{l.shift === "NIGHT" ? "Night" : "Day"}</td>
                      <td>{titleCase(l.deploymentType)}{l.endDate ? ` (to ${dayjs(l.endDate).format("DD MMM")})` : ""}</td>
                      <td>{titleCase(String(l.schedule).replace("_", " "))}</td>
                      <td className="text-end">{l.qty}</td>
                      <td>
                        {canEdit ? (
                          <input
                            type="number"
                            min={0}
                            max={l.qty}
                            className="form-control form-control-sm"
                            value={deployedQty[l.lineId] ?? ""}
                            onChange={(e) => setDeployedQty((prev) => ({ ...prev, [l.lineId]: e.target.value }))}
                          />
                        ) : (
                          l.deployedQty
                        )}
                      </td>
                      <td>
                        <span className={`badge ${l.status === "DEPLOYED" ? "badge-linesuccess" : l.status === "PARTIAL" ? "badge-linewarning" : "badge-linelight"}`}>
                          {titleCase(l.status)} {l.deployedQty}/{l.qty}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {canEdit ? (
        <form onSubmit={handleSubmit(wholeOrder ? onSubmitWhole : onSubmitLines)} className="card">
          <div className="card-body">
            <h5 className="mb-1">Mark Deployed</h5>
            <p className="text-muted small mb-3">
              {wholeOrder
                ? "This order has no guard lines, so the whole order is marked deployed."
                : "These details apply to every line in this request. Lower a line's Deployed figure to record a partial deployment."}
            </p>
            <div className="row">
              <div className="col-md-4 mb-3">
                <label className="form-label">Deployment Date</label>
                <input type="date" className="form-control" {...register("deploymentDate", { required: true })} />
                {errors.deploymentDate && <div className="text-danger small">Deployment date is required</div>}
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Deployment Number</label>
                <input type="text" className="form-control" maxLength={50} {...register("deploymentNumber")} />
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Remarks</label>
                <input type="text" className="form-control" maxLength={300} {...register("remarks")} />
              </div>
            </div>
            <button type="submit" className="btn btn-primary" disabled={isSaving || (!wholeOrder && linesLoading)}>
              {isSaving ? "Saving..." : "Mark Deployed"}
            </button>
          </div>
        </form>
      ) : (
        <p className="text-muted">You don't have permission to record a guard deployment.</p>
      )}

      {showRoster && <GuardRosterModal order={request} onClose={() => setShowRoster(false)} />}
      <CreateAssignmentsFromDeploymentModal
        show={showCreateAssignments}
        order={request}
        onClose={() => setShowCreateAssignments(false)}
      />
    </div>
  );
};

export default PendingDeploymentDetail;
